/**
 * Store Offers API — typed wrappers around `/api/retaileroffers/*`.
 *
 * Ported from legacy `frontend/src/services/retailerOffersService.ts`. The
 * `formatOfferPrice`, `calculateSavingsPercent`, `hasActiveDeal` display
 * helpers from the legacy service are intentionally NOT ported here —
 * they're UI formatting concerns, handled in apps/web or a shared UI
 * module (T8).
 */

import type { HttpClient } from './client';
import {
  type SaveOfferToHouseholdProductsRequest,
  type SearchRetailerOffersOptions,
} from '../types/requests';
import {
  type HouseholdProductWithDetails,
  myProductWithDetailsSchema,
} from '../types/HouseholdProduct';
import {
  type RetailerOffersResponse,
  type RetailerOfferWithDetails,
  retailerOfferWithDetailsSchema,
  retailerOffersResponseSchema,
} from '../types/RetailerOffer';
import { z } from 'zod';

const API_BASE = '/api/retaileroffers';

const saveToHouseholdProductsResponseSchema = z.object({
  myProduct: myProductWithDetailsSchema,
  linkedOfferId: z.string(),
});
export type SaveToHouseholdProductsResponse = z.infer<typeof saveToHouseholdProductsResponseSchema>;

export class RetailerOffersApi {
  constructor(private readonly http: HttpClient) {}

  fetchByStore(storeId: string): Promise<RetailerOffersResponse> {
    return this.http.get(
      `${API_BASE}/store/${encodeURIComponent(storeId)}`,
      retailerOffersResponseSchema
    );
  }

  fetch(storeId: string, offerId: string): Promise<RetailerOfferWithDetails> {
    return this.http.get(
      `${API_BASE}/store/${encodeURIComponent(storeId)}/${encodeURIComponent(offerId)}`,
      retailerOfferWithDetailsSchema
    );
  }

  search(
    query: string,
    options?: SearchRetailerOffersOptions
  ): Promise<RetailerOffersResponse> {
    return this.http.get(`${API_BASE}/search`, retailerOffersResponseSchema, {
      q: query,
      storelocation_ids: options?.storeId,
      with_deals: options?.withDeals,
      limit: options?.limit,
    });
  }

  /**
   * Create a HouseholdProduct from a RetailerOffer (and link the offer). The legacy
   * endpoint preserves this as a single atomic operation server-side so
   * clients never end up with a HouseholdProduct that has no offers.
   */
  saveToHouseholdProducts(
    input: SaveOfferToHouseholdProductsRequest
  ): Promise<SaveToHouseholdProductsResponse> {
    return this.http.post(
      `${API_BASE}/save-to-household-products`,
      input,
      saveToHouseholdProductsResponseSchema
    );
  }
}

export type { HouseholdProductWithDetails };
